import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import { httpsCallable } from "firebase/functions";
import { loginWithEmailAndPassword, loginWithGoogle } from "../services/firebaseAuthService";
import { auth, functions } from "../services/firebase";
import { useAuth } from "../hooks/useAuth";
import I_IMC_Props from "../infos/I_IMC_Props";
import { Globals } from "../globals";
import { useTranslation } from "react-i18next";
import { APP_VERSION } from "../version";

export default function Login({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, loading: authLoading, isSuperAdmin, gemeindeId } = useAuth();
    const [loading, setLoading] = useState(false);
    // "Passwort vergessen" (GOD-38): asks for a reset link by email.
    const [resetOpen, setResetOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetState, setResetState] = useState<"idle" | "sending" | "sent">("idle");

    function openReset() {
        const typed = (document.getElementById("loginForm.EmailInput") as HTMLInputElement | null)?.value ?? "";
        setResetEmail(typed);
        setResetState("idle");
        setResetOpen(true);
    }

    async function handleResetRequest(e: FormEvent) {
        e.preventDefault();
        setResetState("sending");
        try {
            const request = httpsCallable<{ email: string }, { success: boolean }>(functions, "requestPasswordReset");
            await request({ email: resetEmail.trim() });
            setResetState("sent");
        } catch (err) {
            setResetState("idle");
            setMessage(err instanceof Error ? err.message : Globals.ERROR_AN_ERROR_OCCURED);
            setVariant("warning");
            setShowMessage(true);
        }
    }

    // Where a logged-in user lands depends on their role/assignment. Navigation is
    // driven by the effect below (once auth + claims have resolved), not inline,
    // because the claims aren't available the instant sign-in resolves.
    async function handleLogin(e: FormEvent) {
        e.preventDefault();
        setLoading(true);
        const form = e.target as HTMLFormElement;
        const email = (form.elements.namedItem("loginForm.EmailInput") as HTMLInputElement).value;
        const password = (form.elements.namedItem("loginForm.PasswordInput") as HTMLInputElement).value;
        try {
            await loginWithEmailAndPassword(email, password);
            setLoading(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : Globals.ERROR_AN_ERROR_OCCURED;
            setMessage(errorMessage);
            setVariant("warning");
            setShowMessage(true);
            document.getElementById("loginForm.PasswordInput")?.focus();
            setLoading(false);
        }
    }

    async function handleGoogleLogin() {
        setLoading(true);
        try {
            await loginWithGoogle();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : Globals.ERROR_AN_ERROR_OCCURED;
            setMessage(errorMessage);
            setVariant("warning");
            setShowMessage(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const onLoginPage = location.pathname === Globals.PATH_DEFAULT;
        const fromLogout = location.state?.fromLogout === true;
        if (onLoginPage && !authLoading && isAuthenticated && !fromLogout && auth.currentUser) {
            // Superadmin → Gemeinde-/Nutzerverwaltung; assigned user → service list;
            // unassigned user → holding page (no data access).
            if (isSuperAdmin) {
                navigate(Globals.PATH_SUPERADMIN, { replace: true });
            } else if (gemeindeId) {
                navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST, { replace: true });
            } else {
                navigate(Globals.PATH_NO_GEMEINDE, { replace: true });
            }
        }
    }, [isAuthenticated, authLoading, isSuperAdmin, gemeindeId, navigate, location.pathname, location.state]);

    return (
        <>
            <title>{t("auth.signIn")}</title>
            <div className="gl-login-wrap">
                <div className="gl-login-card">
                    <div className="text-center mb-4">
                        <h1 className="gl-wordmark mb-2" style={{ fontSize: 32 }}>
                            {t("app.name")}
                        </h1>
                        <p className="text-muted small mb-2">{t("app.tagline")}</p>
                        <p className="text-muted small mb-0">{t("app.description")}</p>
                    </div>

                    <h2 className="text-center mb-3" style={{ fontSize: "1.1rem" }}>{t("common.login")}</h2>

                    <div className="card">
                        <div className="card-body">
                            <Form onSubmit={handleLogin}>
                                <Form.Group className="mb-3" controlId="loginForm.EmailInput">
                                    <Form.Label>{t("auth.email")}</Form.Label>
                                    <Form.Control type="email" placeholder={t("auth.emailPlaceholder") as string} />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="loginForm.PasswordInput">
                                    <Form.Label>{t("auth.password")}</Form.Label>
                                    <Form.Control type="password" placeholder={t("auth.passwordPlaceholder") as string} />
                                </Form.Group>
                                <div className="text-end mb-3" style={{ marginTop: "-0.5rem" }}>
                                    <Button variant="link" size="sm" className="p-0" onClick={openReset} data-testid="forgot-password">
                                        {t("auth.forgotPassword")}
                                    </Button>
                                </div>
                                <Button type="submit" variant="primary" disabled={loading || authLoading} className="w-100 mb-3">
                                    {t("common.login")}{" "}
                                    {(loading || authLoading) && (
                                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                    )}
                                </Button>
                            </Form>

                            {resetOpen && (
                                <div className="border rounded p-3 mb-3" data-testid="password-reset">
                                    {resetState === "sent" ? (
                                        <p className="small mb-0" role="status">{t("auth.resetSent")}</p>
                                    ) : (
                                        <Form onSubmit={handleResetRequest}>
                                            <p className="small text-muted mb-2">{t("auth.resetIntro")}</p>
                                            <Form.Group className="mb-2" controlId="resetForm.EmailInput">
                                                <Form.Label className="visually-hidden">{t("auth.email")}</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    required
                                                    value={resetEmail}
                                                    onChange={(e) => setResetEmail(e.target.value)}
                                                    placeholder={t("auth.emailPlaceholder") as string}
                                                />
                                            </Form.Group>
                                            <div className="d-flex gap-2">
                                                <Button type="submit" size="sm" disabled={resetState === "sending"}>
                                                    {t("auth.resetSend")}{" "}
                                                    {resetState === "sending" && <Spinner as="span" animation="grow" size="sm" aria-hidden="true" />}
                                                </Button>
                                                <Button size="sm" variant="outline-secondary" onClick={() => setResetOpen(false)}>
                                                    {t("common.cancel")}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </div>
                            )}

                            <div className="d-flex align-items-center gap-2 my-3">
                                <hr className="flex-grow-1 m-0" />
                                <span className="gl-meta">{t("auth.or")}</span>
                                <hr className="flex-grow-1 m-0" />
                            </div>

                            <Button
                                variant="outline-primary"
                                onClick={handleGoogleLogin}
                                disabled={loading || authLoading}
                                className="w-100 d-inline-flex align-items-center justify-content-center gap-2"
                            >
                                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                                    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                                    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                                    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.991 21.991 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                                    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/>
                                </svg>
                                {t("auth.loginWithGoogle")}{" "}
                                {(loading || authLoading) && (
                                    <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                )}
                            </Button>
                        </div>
                    </div>

                    <p className="text-center mt-4 mb-1">
                        <Link to={Globals.PATH_DATENSCHUTZ}>{t("app.privacyLink")}</Link>
                        {" · "}
                        <Link to={Globals.PATH_IMPRESSUM}>{t("footer.impressum")}</Link>
                    </p>
                    <p className="text-center mb-0 gl-meta">v{APP_VERSION}</p>
                </div>
            </div>
        </>
    );
}
